import { JobPaymentModal } from '../../components/payment/JobPaymentModal';
import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  Star,
  Clock,
  Wrench,
  CheckCircle2,
  Calendar,
  MessageSquare,
  ArrowLeft,
  AlertTriangle,
  Award,
  DollarSign,
  Eye,
  RefreshCw,
  X,
} from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { VerificationBadge } from '../../components/common/VerificationBadge';
import {
  checkJobPaymentStatus,
  fetchTemporaryCompletionPhoto,
  discardTemporaryCompletionPhoto,
  releaseJobPaymentRpc,
  rejectSupabaseQuote,
} from '../../lib/supabaseService';
import { JobRatingModal } from '../../components/jobs/JobRatingModal';
import { RealTimeJobStatusTracker } from '../../components/jobs/RealTimeJobStatusTracker';
import { ShareJobButton } from '../../components/common/ShareJobButton';

export const QuoteComparisonPage: React.FC = () => {
  const { jobs, quotes, profiles, viewParams, selectProfessionalAndAcceptQuote, navigateTo, currentUser, refreshJobsAndQuotes } = useApp();

  const currentJob = jobs.find((j) => j.id === viewParams.jobId) || jobs[0];
  const jobQuotes = quotes.filter((q) => q.jobId === currentJob?.id);

  const [confirmingQuoteId, setConfirmingQuoteId] = useState<string | null>(null);
  const [rejectingQuoteId, setRejectingQuoteId] = useState<string | null>(null);
  const [selectedQuoteForPayment, setSelectedQuoteForPayment] = useState<any | null>(null);
  const [filterMode, setFilterMode] = useState<'all' | 'within_budget'>('all');
  const [sortBy, setSortBy] = useState<'price_asc' | 'rating_desc' | 'duration_asc'>('price_asc');
  const [authoritativePayment, setAuthoritativePayment] = useState<{ isPaid: boolean; checking: boolean }>({
    isPaid: false,
    checking: true,
  });

  // Payment Release & Evidence State
  const [showReleaseModal, setShowReleaseModal] = useState(false);
  const [hasAuthorizedRelease, setHasAuthorizedRelease] = useState(false);
  const [completionNotes, setCompletionNotes] = useState('');
  const [isConfirmingCompletion, setIsConfirmingCompletion] = useState(false);
  const [releaseFeedback, setReleaseFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [completionPhoto, setCompletionPhoto] = useState<string | null>(null);
  const [loadingPhoto, setLoadingPhoto] = useState(false);
  const [previewFullscreenPhoto, setPreviewFullscreenPhoto] = useState<string | null>(null);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [isReleasedLocal, setIsReleasedLocal] = useState(false);

  const isJobReleased = Boolean(
    isReleasedLocal ||
      currentJob?.payment_released ||
      (currentJob as any)?.paymentReleased ||
      currentJob?.status === 'completed' ||
      (currentJob as any)?.payment_status === 'released' ||
      currentJob?.paymentStatus === 'released'
  );

  const isJobAlreadyPaid = Boolean(
    isJobReleased ||
      authoritativePayment.isPaid ||
      currentJob?.status === 'in_progress' ||
      currentJob?.status === 'completed_by_professional' ||
      currentJob?.paymentStatus === 'held_in_escrow' ||
      (currentJob as any)?.payment_status === 'held_in_escrow' ||
      (currentJob as any)?.payment_status === 'paid' ||
      currentJob?.escrow_held === true ||
      Boolean(currentJob?.payment_reference)
  );

  const isProMarkedCompletedWithPhoto = Boolean(
    !isJobReleased &&
      (currentJob?.completed_by_professional ||
        currentJob?.status === 'completed_by_professional' ||
        currentJob?.completionStatus === 'completed_by_professional' ||
        (currentJob as any)?.completion_status === 'awaiting_customer_confirmation' ||
        (currentJob as any)?.completion_status === 'completed_by_professional') &&
      Boolean(
        completionPhoto ||
          currentJob?.completionPhotoUrl ||
          (currentJob as any)?.completion_photo_url ||
          currentJob?.has_completion_photo ||
          (currentJob as any)?.completion_photo_uploaded
      )
  );

  useEffect(() => {
    if (currentJob?.id) {
      checkJobPaymentStatus(currentJob.id).then((res) => {
        setAuthoritativePayment({ isPaid: Boolean(res.isPaid), checking: false });
      });
      fetchTemporaryCompletionPhoto(currentJob.id)
        .then((url) => {
          if (url) setCompletionPhoto(url);
        })
        .catch(() => {});
    }
  }, [currentJob?.id, currentJob?.status, currentJob?.completed_by_professional]);

  // Load completion photo evidence when Payment Release Modal is opened
  useEffect(() => {
    if (showReleaseModal && currentJob?.id) {
      setHasAuthorizedRelease(false);
      setLoadingPhoto(true);
      fetchTemporaryCompletionPhoto(currentJob.id)
        .then((url) => {
          if (url) {
            setCompletionPhoto(url);
          } else if ((currentJob as any).completion_photo_url || (currentJob as any).completionPhotoUrl) {
            setCompletionPhoto((currentJob as any).completion_photo_url || (currentJob as any).completionPhotoUrl);
          } else {
            setCompletionPhoto(null);
          }
        })
        .finally(() => setLoadingPhoto(false));
    }
  }, [showReleaseModal, currentJob?.id]);

  // Real-time job status tracking auto-sync interval
  useEffect(() => {
    const pollTimer = setInterval(() => {
      if (refreshJobsAndQuotes) {
        refreshJobsAndQuotes();
      }
    }, 10000);
    return () => clearInterval(pollTimer);
  }, [refreshJobsAndQuotes]);

  const hiredQuote = jobQuotes.find(
    (q) => q.status === 'accepted' || (currentJob?.selectedProfessionalId && q.professionalId === currentJob.selectedProfessionalId)
  ) || jobQuotes[0];

  const grossAmount = Number(hiredQuote?.totalAmount || currentJob?.budgetEstimate || 0);
  const platformShare = Math.round((grossAmount * 10) / 100);
  const artisanNetShare = grossAmount - platformShare;

  const handleReleasePayment = async () => {
    if (!currentUser?.id || !currentJob?.id) return;
    setIsConfirmingCompletion(true);
    setReleaseFeedback(null);
    try {
      const res = await releaseJobPaymentRpc(currentJob.id, completionNotes, currentUser.id);
      if (res.success) {
        setReleaseFeedback({
          type: 'success',
          message: res.message || 'Payment released successfully! 90% payout has been credited to the professional.',
        });
        setShowReleaseModal(false);
        setCompletionNotes('');
        setCompletionPhoto(null);
        setIsReleasedLocal(true);
        discardTemporaryCompletionPhoto(currentJob.id).catch(() => {});
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
        checkJobPaymentStatus(currentJob.id).then((pRes) => {
          setAuthoritativePayment({ isPaid: Boolean(pRes.isPaid), checking: false });
        });
        setTimeout(() => {
          setShowRatingModal(true);
        }, 600);
      } else {
        setReleaseFeedback({
          type: 'error',
          message: res.error || 'Failed to release payment.',
        });
      }
    } catch (err: any) {
      setReleaseFeedback({
        type: 'error',
        message: err.message || 'Unable to communicate with server.',
      });
    } finally {
      setIsConfirmingCompletion(false);
    }
  };

  const targetBudget = Number(currentJob?.budgetEstimate || 0);

  const displayedQuotes = React.useMemo(() => {
    let list = [...jobQuotes];
    if (filterMode === 'within_budget' && targetBudget > 0) {
      list = list.filter((q) => q.totalAmount <= targetBudget);
    }
    list.sort((a, b) => {
      if (sortBy === 'price_asc') return a.totalAmount - b.totalAmount;
      if (sortBy === 'rating_desc') return b.professionalRating - a.professionalRating;
      if (sortBy === 'duration_asc') return a.durationEstimate.localeCompare(b.durationEstimate);
      return 0;
    });
    return list;
  }, [jobQuotes, filterMode, sortBy, targetBudget]);

  if (!currentJob) {
    return (
      <div className="max-w-4xl mx-auto py-12 px-4 text-center">
        <h2 className="text-xl font-bold text-slate-800">No Job Selected</h2>
        <button
          onClick={() => navigateTo('customer_dashboard')}
          className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  const handleHireConfirm = async (quoteId: string) => {
    const q = jobQuotes.find((item) => item.id === quoteId);
    if (q) {
      setConfirmingQuoteId(null);
      try {
        await selectProfessionalAndAcceptQuote(quoteId, { skipNavigate: true });
      } catch {
        // Proceed to open payment modal even if already accepted
      }
      setSelectedQuoteForPayment(q);
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    setRejectingQuoteId(quoteId);
    try {
      const res = await rejectSupabaseQuote(quoteId, currentJob.id, currentUser?.id || currentJob.customerId);
      if (res.success) {
        setReleaseFeedback({
          type: 'success',
          message: 'Quotation rejected.',
        });
        if (refreshJobsAndQuotes) {
          await refreshJobsAndQuotes();
        }
      } else {
        setReleaseFeedback({
          type: 'error',
          message: res.error || 'Unable to reject quotation.',
        });
      }
    } finally {
      setRejectingQuoteId(null);
    }
  };

  const displayJobReference =
    currentJob.jobReference ||
    (currentJob as any).job_reference ||
    `JM-${String(currentJob.id || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`;

    const isJobHired = Boolean(
      authoritativePayment.isPaid ||
      (currentJob.selectedProfessionalId && (currentJob.status === 'in_progress' || currentJob.status === 'assigned' || currentJob.status === 'completed')) ||
      currentJob.status === 'completed'
    );

    return (
      <div className="max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8 py-4 sm:py-8 space-y-6 sm:space-y-8">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <button
              onClick={() => navigateTo('customer_dashboard')}
              className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2 py-1 cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to My Jobs</span>
            </button>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-xl sm:text-3xl font-extrabold text-slate-900">
                Quote Comparison & Professional Selection
              </h1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                {jobQuotes.length} Quotes Received
              </span>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-mono font-bold bg-slate-100 text-slate-700 border border-slate-200">
                Ref: {displayJobReference}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Job Request: <strong>{currentJob.title}</strong> ({currentJob.lga}, {currentJob.state})
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 self-start sm:self-auto">
            {currentJob && (
              <ShareJobButton job={currentJob} variant="outline" showDropdown={true} />
            )}
            <div className="bg-white p-3 rounded-2xl border border-slate-200 text-xs sm:text-right">
              <span className="text-[10px] uppercase text-slate-400 block">Your Budget Target</span>
              <span className="font-extrabold text-slate-900 text-base">
                ₦{(currentJob.budgetEstimate || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Release Payment Notification Feedback */}
        {releaseFeedback && (
          <div
            className={`p-4 rounded-2xl text-xs font-bold flex items-center justify-between gap-3 shadow-xs ${
              releaseFeedback.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-900'
                : 'bg-rose-50 border border-rose-200 text-rose-900'
            }`}
          >
            <div className="flex items-center gap-2">
              {releaseFeedback.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
              )}
              <span>{releaseFeedback.message}</span>
            </div>
            <button
              onClick={() => setReleaseFeedback(null)}
              className="text-slate-400 hover:text-slate-700 font-mono text-sm cursor-pointer"
            >
              ✕
            </button>
          </div>
        )}

        {/* Real-Time Job Status Tracker Component */}
        <RealTimeJobStatusTracker
          job={currentJob}
          quotes={jobQuotes}
          onRefresh={refreshJobsAndQuotes}
          onRateJob={() => setShowRatingModal(true)}
        />

        {/* Professional Hired Escrow Status Banner */}
        {isJobHired && (
          <div className="p-4 sm:p-5 rounded-2xl sm:rounded-3xl bg-emerald-50 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-fade-in shadow-xs">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-xl text-emerald-700 shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-xs sm:text-sm font-black text-emerald-950">
                  {currentJob.payment_released || currentJob.status === 'completed'
                    ? 'Work Confirmed & Payment Released!'
                    : (currentJob.completed_by_professional || currentJob.status === 'completed_by_professional')
                    ? '📷 Artisan Marked Work Complete - Release Payment Available'
                    : 'Professional Hired & Escrow Secured!'}
                </h4>
                <p className="text-xs text-emerald-800">
                  {currentJob.payment_released || currentJob.status === 'completed'
                    ? 'Funds have been safely disbursed to the professional. Thank you for using JobMatch NG!'
                    : (currentJob.completed_by_professional || currentJob.status === 'completed_by_professional')
                    ? 'Your artisan has submitted completion photo evidence. Review evidence and release the 90% payment below.'
                    : 'This job is in progress with your hired artisan. Use Job Chat to coordinate arrival and execution.'}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() =>
                  navigateTo('messages', {
                    jobId: currentJob.id,
                    proId: currentJob.selectedProfessionalId,
                  })
                }
                className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold flex items-center justify-center gap-2 cursor-pointer min-h-[44px]"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Open Job Chat</span>
              </button>

              {isJobReleased ? (
                <div className="px-4 py-2.5 rounded-xl bg-emerald-950 text-emerald-300 font-extrabold text-xs border border-emerald-700/80 flex items-center gap-1.5 shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span>Payment Has Been Released</span>
                </div>
              ) : isProMarkedCompletedWithPhoto ? (
                <button
                  type="button"
                  id="release-payment-top-banner-btn"
                  onClick={() => {
                    setShowReleaseModal(true);
                    setReleaseFeedback(null);
                  }}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold flex items-center justify-center gap-2 shadow-md cursor-pointer transition-all min-h-[44px]"
                >
                  <DollarSign className="w-4 h-4 text-white" />
                  <span>Release Payment (90%)</span>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    navigateTo('customer_dashboard', {
                      tab: 'active',
                      activeTab: 'active',
                      jobId: currentJob.id,
                    })
                  }
                  className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold flex items-center justify-center gap-2 shadow-xs cursor-pointer transition-all min-h-[44px]"
                >
                  <Clock className="w-4 h-4 text-white" />
                  <span>View in Active Jobs</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Quotes Filter & Sort Controls */}
        {jobQuotes.length > 0 && (
          <div className="bg-white p-3 sm:p-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-bold text-slate-700">Filter:</span>
              <button
                type="button"
                onClick={() => setFilterMode('all')}
                className={`px-3 py-1.5 rounded-xl font-bold cursor-pointer transition-colors ${
                  filterMode === 'all'
                    ? 'bg-slate-900 text-white shadow-2xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                All Quotes ({jobQuotes.length})
              </button>
              {targetBudget > 0 && (
                <button
                  type="button"
                  onClick={() => setFilterMode('within_budget')}
                  className={`px-3 py-1.5 rounded-xl font-bold cursor-pointer transition-colors ${
                    filterMode === 'within_budget'
                      ? 'bg-emerald-600 text-white shadow-2xs'
                      : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-200'
                  }`}
                >
                  Within Budget ({jobQuotes.filter((q) => q.totalAmount <= targetBudget).length})
                </button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="font-semibold text-slate-500">Sort:</span>
              <select
                value={sortBy}
                onChange={(e: any) => setSortBy(e.target.value)}
                className="px-3 py-1.5 rounded-xl border border-slate-200 bg-white font-bold text-slate-800 focus:outline-hidden focus:border-emerald-500"
              >
                <option value="price_asc">Lowest Price First (₦)</option>
                <option value="rating_desc">Highest Rated Professional</option>
                <option value="duration_asc">Shortest Job Duration</option>
              </select>
            </div>
          </div>
        )}

        {displayedQuotes.length === 0 ? (
          <div className="bg-white rounded-2xl sm:rounded-3xl p-8 sm:p-12 text-center border border-slate-200">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="text-base sm:text-lg font-bold text-slate-900">
              {filterMode === 'within_budget' ? 'No Quotes Within Stated Target Budget' : 'Awaiting Professional Quotes'}
            </h3>
            <p className="text-xs text-slate-500 max-w-md mx-auto mt-1 mb-6">
              {filterMode === 'within_budget'
                ? `None of the received quotes are under ₦${targetBudget.toLocaleString()}. You can switch to All Quotes to review other proposals or adjust your budget.`
                : `Your request was broadcast to verified ${currentJob.categoryName} professionals in ${currentJob.lga}. Quotes normally arrive within 30-45 minutes.`}
            </p>
            {filterMode === 'within_budget' ? (
              <button
                onClick={() => setFilterMode('all')}
                className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs cursor-pointer"
              >
                View All Received Quotes
              </button>
            ) : (
              <button
                onClick={() => navigateTo('customer_dashboard')}
                className="w-full sm:w-auto px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs min-h-[44px] cursor-pointer"
              >
                Return to Dashboard
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {displayedQuotes.map((quote) => {
              const isAccepted = quote.status === 'accepted' || (currentJob.selectedProfessionalId && currentJob.selectedProfessionalId === quote.professionalId);
              const isDeclined = quote.status === 'declined' || (isJobHired && !isAccepted);

            return (
              <div
                key={quote.id}
                id={`quote-card-${quote.id}`}
                className={`bg-white rounded-2xl sm:rounded-3xl p-4 sm:p-6 border transition-all flex flex-col justify-between ${
                  isAccepted
                    ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-md'
                    : isDeclined
                    ? 'border-slate-200 opacity-60 bg-slate-50'
                    : 'border-slate-200 hover:border-slate-300 hover:shadow-lg'
                }`}
              >
                <div>
                  {/* Pro Header */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="relative shrink-0">
                        <img
                          src={quote.professionalAvatar}
                          alt={quote.professionalName}
                          className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl object-cover ring-2 ring-emerald-100"
                        />
                        {profiles.find((p) => p.id === quote.professionalId)?.isProfilePhotoVerified && (
                          <span
                            title="Verified Live Photo"
                            className="absolute -bottom-1 -right-1 px-1.5 py-0.2 rounded-full bg-emerald-600 text-white text-[8px] font-black border border-white shadow-2xs"
                          >
                            Verified
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-bold text-sm sm:text-base text-slate-900">{quote.professionalName}</h3>
                        <p className="text-xs text-slate-500">{quote.professionalBusiness}</p>
                        <div className="mt-1 flex items-center gap-2">
                          <VerificationBadge
                            profile={profiles.find((p) => p.id === quote.professionalId)}
                            status={quote.professionalVerification}
                            size="sm"
                          />
                        </div>
                      </div>
                    </div>

                    {isAccepted && (
                      <span className="px-2.5 py-1 rounded-full text-[11px] sm:text-xs font-extrabold bg-emerald-600 text-white shadow-xs shrink-0">
                        HIRED PRO
                      </span>
                    )}
                  </div>

                  {/* Rating & Experience Strip */}
                  <div className="grid grid-cols-2 gap-2 p-2.5 rounded-xl bg-slate-50 border border-slate-100 text-xs mb-4">
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 text-amber-500 fill-amber-500 shrink-0" />
                      <span className="font-bold text-slate-800">
                        {quote.professionalRating.toFixed(1)}
                      </span>
                      <span className="text-slate-400">({quote.professionalReviewCount})</span>
                    </div>

                    <div className="flex items-center gap-1.5 text-slate-600">
                      <Award className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>{quote.professionalYearsExp} Yrs Exp</span>
                    </div>
                  </div>

                  {/* Pricing Comparison Matrix Box */}
                  <div className="p-3.5 sm:p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100/80 mb-4 space-y-2">
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs font-medium text-slate-500">Total Proposed Price</span>
                      <span className="text-xl sm:text-2xl font-extrabold text-emerald-800">
                        ₦{quote.totalAmount.toLocaleString()}
                      </span>
                    </div>

                    <div className="border-t border-emerald-200/50 pt-2 flex justify-between text-xs text-slate-600">
                      <span>Professional Labor: <strong>₦{quote.laborCost.toLocaleString()}</strong></span>
                      <span>Materials: <strong>₦{quote.materialsCost.toLocaleString()}</strong></span>
                    </div>

                    {targetBudget > 0 && (
                      <div className="pt-2 border-t border-emerald-200/60 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500">Budget Match:</span>
                        {quote.totalAmount <= targetBudget ? (
                          <span className="font-bold text-emerald-700 bg-emerald-100/70 px-2 py-0.5 rounded-md">
                            ✓ Within Budget (₦{(targetBudget - quote.totalAmount).toLocaleString()} under)
                          </span>
                        ) : (
                          <span className="font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md">
                            ₦{(quote.totalAmount - targetBudget).toLocaleString()} above target
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Execution Timeline & Warranty */}
                  <div className="space-y-2 text-xs text-slate-600 mb-4">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 pb-1 border-b border-slate-100">
                      <span className="font-mono font-bold text-slate-700">
                        Quote Ref: {quote.quoteReference || (quote as any).quote_reference || `QT-${String(quote.id || '').replace(/[^a-zA-Z0-9]/g, '').slice(0, 8).toUpperCase()}`}
                      </span>
                      <span>
                        Submitted:{' '}
                        {quote.createdAt
                          ? new Date(quote.createdAt).toLocaleDateString('en-NG', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })
                          : 'Recently'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span>
                        Estimated Arrival Time: <strong>{quote.estimatedArrivalTime || quote.startDate}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span>Est. Duration: <strong>{quote.durationEstimate}</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                      <span>
                        Workmanship Guarantee: <strong>{quote.warrantyDays} Days Warranty</strong>
                      </span>
                    </div>
                  </div>

                  {/* Pro Note */}
                  <div className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-100 leading-relaxed mb-4">
                    <strong className="text-slate-800 block mb-1">Artisan Message &amp; Proposal Note:</strong>
                    "{quote.notes}"
                  </div>
                </div>

                {/* Bottom Actions */}
                <div className="pt-4 border-t border-slate-100 space-y-2">
                  {confirmingQuoteId === quote.id ? (
                    <div className="p-3 bg-emerald-100 rounded-xl space-y-2 text-center">
                      <p className="text-xs font-bold text-emerald-900">
                        Confirm hiring {quote.professionalName} for ₦{quote.totalAmount.toLocaleString()}?
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                        <button
                          onClick={() => handleHireConfirm(quote.id)}
                          className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-xs min-h-[44px] cursor-pointer"
                        >
                          Yes, Confirm & Hire
                        </button>
                        <button
                          onClick={() => setConfirmingQuoteId(null)}
                          className="w-full sm:w-auto px-4 py-2.5 rounded-xl bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium text-xs min-h-[44px] cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : isAccepted ? (
                    <div className="space-y-2">
                      {!isJobAlreadyPaid && !isJobReleased && (
                        <button
                          type="button"
                          onClick={() => setSelectedQuoteForPayment(quote)}
                          className="w-full py-2.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-black text-xs shadow-md flex items-center justify-center gap-2 min-h-[44px] cursor-pointer transition-all"
                        >
                          <DollarSign className="w-4 h-4 text-white" />
                          <span>Complete Escrow Payment (₦{quote.totalAmount.toLocaleString()})</span>
                        </button>
                      )}
                      <button
                        onClick={() =>
                          navigateTo('messages', {
                            jobId: quote.jobId,
                            proId: quote.professionalId,
                          })
                        }
                        className="w-full py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 min-h-[44px] cursor-pointer"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>Open Chat with Professional</span>
                      </button>

                      {isJobReleased ? (
                        <div className="w-full py-2 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-extrabold text-xs flex items-center justify-center gap-1.5">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>Payment Released (Completed)</span>
                        </div>
                      ) : isProMarkedCompletedWithPhoto ? (
                        <button
                          type="button"
                          id="release-payment-card-btn"
                          onClick={() => {
                            setShowReleaseModal(true);
                            setReleaseFeedback(null);
                          }}
                          className="w-full py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md flex items-center justify-center gap-2 min-h-[44px] cursor-pointer transition-all"
                        >
                          <DollarSign className="w-4 h-4 text-white" />
                          <span>Release Payment to Professional (90%)</span>
                        </button>
                      ) : isJobAlreadyPaid ? (
                        <div className="w-full py-2.5 px-3 rounded-xl bg-blue-50 border border-blue-200 text-blue-900 font-bold text-[11px] flex items-center justify-center gap-1.5 text-center">
                          <Clock className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                          <span>Job In Progress — Release unlocks after Pro sends completion photo</span>
                        </div>
                      ) : null}
                    </div>
                  ) : isDeclined || (isJobHired && !isAccepted) ? (
                    <div className="text-center py-2 text-xs text-slate-500 font-medium bg-slate-50 rounded-xl border border-slate-100">
                      {quote.status === 'declined' ? 'Quotation Declined' : 'Another professional was hired for this job'}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row items-stretch gap-2">
                        <button
                          onClick={() => navigateTo('pro_profile', { proId: quote.professionalId })}
                          className="flex-1 py-2.5 px-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs transition-colors min-h-[44px] cursor-pointer"
                        >
                          View Full Profile
                        </button>
                        <button
                          id={`select-pro-${quote.id}-btn`}
                          onClick={() => setConfirmingQuoteId(quote.id)}
                          className="flex-1 py-2.5 px-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-xs transition-colors flex items-center justify-center gap-1.5 min-h-[44px] cursor-pointer"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Select & Hire</span>
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            navigateTo('messages', {
                              jobId: quote.jobId,
                              proId: quote.professionalId,
                            })
                          }
                          className="flex-1 py-2 px-3 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                        >
                          <MessageSquare className="w-3.5 h-3.5 text-slate-500" />
                          <span>Ask Clarification</span>
                        </button>
                        <button
                          type="button"
                          disabled={rejectingQuoteId === quote.id}
                          onClick={() => handleRejectQuote(quote.id)}
                          className="py-2 px-3 rounded-xl border border-rose-200 hover:bg-rose-50 text-rose-700 font-semibold text-xs flex items-center justify-center gap-1 cursor-pointer disabled:opacity-50"
                        >
                          <X className="w-3.5 h-3.5" />
                          <span>{rejectingQuoteId === quote.id ? 'Rejecting...' : 'Reject'}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Secure Escrow Payment Modal */}
      {selectedQuoteForPayment && currentJob && (
        <JobPaymentModal
          isOpen={Boolean(selectedQuoteForPayment)}
          onClose={() => setSelectedQuoteForPayment(null)}
          job={currentJob}
          quote={selectedQuoteForPayment}
          customerId={currentUser?.id || currentJob.customerId}
          customerEmail={currentUser?.email || ''}
          customerName={currentUser?.fullName || currentJob.customerName}
        />
      )}

      {/* Payment Release Confirmation Modal */}
      {showReleaseModal && currentJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in overflow-y-auto">
          <div className="bg-white rounded-3xl max-w-lg w-full p-5 sm:p-7 border border-slate-100 shadow-2xl space-y-4 my-auto relative">
            <div className="flex items-start justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-2xl bg-emerald-100 text-emerald-800">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-black text-slate-900">
                    Payment Release Confirmation
                  </h3>
                  <p className="text-xs text-slate-500">
                    Job Request: <strong>{currentJob.title}</strong>
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowReleaseModal(false)}
                className="p-1.5 rounded-full text-slate-400 hover:text-slate-800 hover:bg-slate-100 cursor-pointer"
              >
                ✕
              </button>
            </div>

            {/* Payout & Escrow Breakdown */}
            <div className="p-4 rounded-2xl bg-emerald-50/80 border border-emerald-200 space-y-2.5">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[10px] font-bold uppercase text-emerald-800 tracking-wider block">
                    Artisan Direct Net Payout (90%)
                  </span>
                  <span className="text-2xl font-black text-emerald-950">
                    ₦{artisanNetShare.toLocaleString()}
                  </span>
                </div>
                <span className="px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-900 border border-emerald-300">
                  Escrow Protected
                </span>
              </div>

              <div className="pt-2 border-t border-emerald-200/70 space-y-1 text-xs text-slate-600">
                <div className="flex items-center justify-between">
                  <span>Total Escrow Deposit (100%):</span>
                  <span className="font-bold text-slate-800">₦{grossAmount.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500">
                  <span>Platform Commission (10%):</span>
                  <span>₦{platformShare.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Work Completion Photo Evidence */}
            <div className="p-3.5 bg-purple-50/80 border border-purple-200 rounded-2xl space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-purple-950 flex items-center gap-1.5">
                  <span>📷 Work Completion Photo Evidence:</span>
                </span>
                {completionPhoto && (
                  <button
                    type="button"
                    onClick={() => setPreviewFullscreenPhoto(completionPhoto)}
                    className="text-[11px] font-bold text-purple-700 hover:text-purple-900 underline flex items-center gap-1 cursor-pointer"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    <span>View Full Size</span>
                  </button>
                )}
              </div>

              {loadingPhoto ? (
                <div className="p-6 bg-white rounded-xl border border-dashed border-purple-200 text-center flex flex-col items-center justify-center gap-2">
                  <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
                  <span className="text-xs text-purple-800 font-medium">Fetching completion photo evidence from server...</span>
                </div>
              ) : completionPhoto ? (
                <div
                  onClick={() => setPreviewFullscreenPhoto(completionPhoto)}
                  className="rounded-xl overflow-hidden border border-purple-300 max-h-56 bg-slate-950 cursor-pointer relative group"
                  title="Click to view full size"
                >
                  <img
                    src={completionPhoto}
                    alt="Work completion evidence uploaded by professional"
                    className="w-full h-full object-contain mx-auto max-h-56 group-hover:opacity-95 transition-opacity"
                  />
                  <div className="absolute inset-0 bg-slate-900/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-xs font-bold gap-1.5">
                    <Eye className="w-4 h-4" />
                    <span>Click to Zoom</span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-white rounded-xl border border-dashed border-amber-300 text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <span>No completion photo proof found. A verified completion photo is recommended before funds are released.</span>
                </div>
              )}
            </div>

            {/* Checkbox Acknowledgement */}
            <label className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 cursor-pointer transition-all">
              <input
                type="checkbox"
                checked={hasAuthorizedRelease}
                onChange={(e) => setHasAuthorizedRelease(e.target.checked)}
                className="mt-0.5 w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500 cursor-pointer shrink-0"
              />
              <span className="text-xs text-slate-800 font-semibold select-none leading-relaxed">
                I confirm that this work has been completed satisfactorily, I have reviewed the photo evidence, and I authorize the release of the 90% payment to the professional.
              </span>
            </label>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">
                Optional Feedback & Rating Notes
              </label>
              <textarea
                value={completionNotes}
                onChange={(e) => setCompletionNotes(e.target.value)}
                placeholder="e.g., Artisan arrived on time, was courteous, and solved the fault cleanly."
                rows={2}
                className="w-full p-3 rounded-xl border border-slate-200 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden resize-none"
              />
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowReleaseModal(false)}
                disabled={isConfirmingCompletion}
                className="w-full sm:flex-1 py-3 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer min-h-[44px] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleReleasePayment}
                disabled={!hasAuthorizedRelease || isConfirmingCompletion}
                className="w-full sm:flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md cursor-pointer min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isConfirmingCompletion ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin text-white" />
                    <span>Processing 90% Payment Release...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-white" />
                    <span>Authorize & Release Payment (90%)</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen Completion Photo Preview Modal */}
      {previewFullscreenPhoto && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-slate-950/90 backdrop-blur-sm animate-fade-in"
          onClick={() => setPreviewFullscreenPhoto(null)}
        >
          <div
            className="relative max-w-3xl w-full max-h-[90vh] bg-slate-900 rounded-3xl overflow-hidden border border-slate-800 shadow-2xl flex flex-col items-center justify-center p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-4 right-4 z-10">
              <button
                type="button"
                onClick={() => setPreviewFullscreenPhoto(null)}
                className="px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-white text-xs font-bold cursor-pointer backdrop-blur-xs transition-colors"
              >
                ✕ Close Preview
              </button>
            </div>
            <img
              src={previewFullscreenPhoto}
              alt="Fullscreen completion evidence"
              className="w-full h-full object-contain max-h-[82vh] rounded-2xl"
            />
          </div>
        </div>
      )}

      {/* 1-5 Star Artisan Rating Modal */}
      {showRatingModal && currentJob && (
        <JobRatingModal
          job={currentJob}
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
        />
      )}
    </div>
  );
};
